import { useEffect, useState } from 'react';
import { Landmark, CalendarDays, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ownerGetRevenue } from '@/lib/api';
import { formatCents } from '@/lib/money';
import { ADMIN_REVENUE as T } from '@/constants/testIds';

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <p className="font-display text-3xl mt-2 text-[hsl(var(--lxra-gold))]">{value}</p>
    {hint && <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1">{hint}</p>}
  </div>
);

/** LXRA owner-only: platform-wide commission dashboard. This is where your
 * cut of every marketplace photographer's bookings shows up. Fully simulated
 * (no payment processor) — see routers/payments.py for the ledger logic. */
export const AdminRevenueTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => { ownerGetRevenue().then(setData).catch(() => setData({ studios: [], recent_payouts: [], commission_percent: 20, platform_fee_lifetime_cents: 0, platform_fee_this_month_cents: 0 })); }, []);

  if (!data) return <Skeleton className="h-96 rounded-2xl bg-[#141416]" />;

  return (
    <div className="space-y-6" data-testid={T.section}>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Marketplace</p>
        <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Revenue</h2>
        <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Your {data.commission_percent}% commission across every independent photographer on the marketplace. LXRA's own direct bookings aren't charged a fee.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Landmark} label="Lifetime commission" value={formatCents(data.platform_fee_lifetime_cents)} hint="All marketplace photographers, all time" testId={T.lifetimeFee} />
        <StatCard icon={CalendarDays} label="This month" value={formatCents(data.platform_fee_this_month_cents)} testId={T.monthFee} />
        <StatCard icon={TrendingUp} label="Active earning studios" value={String(data.studios.filter((s) => s.studio_id !== 'lxra').length)} hint="With at least one payment recorded" />
      </div>

      <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden">
        <div className="px-6 pt-5"><h3 className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))]">By studio</h3></div>
        {data.studios.length === 0 ? (
          <p data-testid={T.empty} className="text-sm text-[hsl(var(--lxra-muted))] p-6">No payments recorded yet across the marketplace.</p>
        ) : (
          <Table data-testid={T.studioTable} className="mt-2">
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Studio</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Gross collected</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">LXRA fee</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Photographer net</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Pending</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Available</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.studios.map((s) => (
                <TableRow key={s.studio_id} data-testid={T.studioRow(s.studio_id)} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                  <TableCell>
                    <p className="text-[hsl(var(--lxra-fg))]">{s.name}</p>
                    {s.studio_id === 'lxra' && <Badge variant="outline" className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] text-[10px] font-normal mt-1">Direct — no fee</Badge>}
                  </TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-fg))]">{formatCents(s.lifetime_gross_cents)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-gold))]">{formatCents(s.lifetime_fee_cents)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-fg))]">{formatCents(s.lifetime_net_cents)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-muted))]">{formatCents(s.pending_cents)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-fg))]">{formatCents(s.available_cents)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
        <h3 className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))] mb-4">Recent payouts</h3>
        {data.recent_payouts.length === 0 ? (
          <p className="text-sm text-[hsl(var(--lxra-muted))]">No payouts yet.</p>
        ) : (
          <ul data-testid={T.payoutsList} className="space-y-3">
            {data.recent_payouts.map((p, i) => (
              <li key={i} className="flex items-center justify-between border-t border-[hsl(var(--lxra-border))] pt-3 first:border-0 first:pt-0 text-sm">
                <span className="text-[hsl(var(--lxra-fg))]">{p.studio_name} · {formatCents(p.amount_cents)}</span>
                <span className="text-[hsl(var(--lxra-muted))] text-xs">{new Date(p.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
